import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import clsx from 'clsx';
import { useBrainStore } from '@/state/brainStore';
import { useSimulationStore } from '@/state/simulationStore';
import { useOpsWorklist } from '@/state/useOpsWorklist';
import {
  OPS_SECTION_KEYS,
  OPS_SECTIONS,
  type OpsItem,
  type OpsSectionKey,
} from '@/domain/opsWorklist';
import OpsWorkModal from '@/components/ops/OpsWorkModal';
import OpsOverridesAudit from '@/components/ops/OpsOverridesAudit';
import { worklistTabClass } from '@/components/worklist/tabClass';
import { opsItemIsActionable } from '@/components/ops/opsActionable';
import { aud, customerName, simDateShort, simTimeFull, studioDisplay } from '@/utils/format';
import { formatStudioDay } from '@/utils/studioDay';
import { opsOverdueTopups } from '@/domain/opsReports';
import OpsThreadPill from '@/components/ops/OpsThreadPill';
import { linkSenderLabel } from '@/domain/depositStatus';
import type { CustomerRecord, InternalDmThread } from '@/domain/types';
import { actorLabel } from '@/domain/actor';
import { missedLinkStateLabel } from '@/domain/missedLinks';

// Ops manager worklist screen (vault System Diagram 09). Layout is the APPROVED
// Option B: a badge tab-bar across the top, the active section's list below at
// full width - a close cousin of the Confirmations worklist. The shell (tv-81c)
// fixed the layout + honest empty states; tv-v4p wires the real per-feeder
// derivations from the store so every tab renders its live rows. Actions
// (tv-o1f), the override tab (tv-i0b) and reports (tv-4cn) land here next.

const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;

// The approved (24 Sep mock) short tab labels, in tab order; the section
// blurbs + feeders still come from OPS_SECTIONS.
const TAB_LABEL: Record<OpsSectionKey | 'overdue-topups', string> = {
  overrides: 'Overrides',
  confirmations: 'Unconfirmed',
  'payby-extensions': 'Pay-by extensions',
  'topup-extensions': 'Top-up extensions',
  'unpaid-artists': 'Unpaid artists',
  'unclosed-links': 'Links >48h',
  'no-rebook': 'No-rebook',
  topups: 'Upcoming top-ups',
  unanswered: 'Unanswered',
  'overdue-topups': 'Overdue top-ups',
};

export default function OpsWorklist() {
  const clockIso = useSimulationStore((s) => s.clockIso);
  // 7kta: ONE derivation shared with the rail's Ops badge (useOpsWorklist), so
  // the badge always equals this page's count. Live via /api/views/ops, scoped
  // by the top-bar switcher (archived studios out on "All", branding-3).
  const { worklist, opsBundle, scopeIds } = useOpsWorklist();
  const topupPlans = opsBundle?.topupPlans ?? {};
  const artists = opsBundle?.artists ?? {};
  const customers = opsBundle?.customers ?? {};
  // W5 mj: the tagged Ops DMs drive each row's thread state.
  const dmThreads = useBrainStore((s) => s.internalDmThreads);

  // tv-4cn (Ops reports, owner view): overdue top-ups the desk is still sitting
  // on — the money at risk. First-pass snapshot: count + total outstanding.
  const overdueReport = useMemo(
    () => opsOverdueTopups({ nowIso: clockIso, topupPlans, studioIds: scopeIds }),
    [clockIso, topupPlans, scopeIds],
  );

  // Overdue top-ups is an owner-VISIBILITY tab, not an actionable buildOpsWorklist
  // feeder — the chase belongs to the responsible agent/sweeper, not Ops (tv-6qv.38).
  // So it rides as an extra tab in the same bar + list area, fed by overdueReport.
  const OVERDUE_TAB = {
    key: 'overdue-topups' as const,
    label: 'Overdue top-ups',
    blurb:
      'Money at risk across the network. Visibility only — the chase is owned by the responsible agent or sweeper, not Ops.',
    feeder: 'owner view · overdue top-ups',
  };
  const TABS = [...OPS_SECTIONS, OVERDUE_TAB];
  const [active, setActive] = useState<OpsSectionKey | 'overdue-topups'>(OPS_SECTIONS[0]!.key);
  const [actionItem, setActionItem] = useState<OpsItem | null>(null);
  // W5 mj: `/ops?item=<id>` (the link tagged on an Ops DM) opens that row: its
  // tab, and its action modal when the row is actionable.
  const [searchParams] = useSearchParams();
  const deepItemId = searchParams.get('item');
  useEffect(() => {
    if (!deepItemId) return;
    for (const key of OPS_SECTION_KEYS) {
      const hit = worklist.sections[key].find((i) => i.id === deepItemId);
      if (!hit) continue;
      setActive(key);
      if (opsItemIsActionable(hit)) setActionItem(hit);
      return;
    }
    // The row may not be loaded yet — re-run when the worklist changes.
  }, [deepItemId, worklist]);
  const activeMeta = TABS.find((s) => s.key === active)!;
  const activeItems = active === 'overdue-topups' ? [] : worklist.sections[active];
  const activeCount = active === 'overdue-topups' ? overdueReport.count : activeItems.length;

  const nameFor = (customerId: string): string => {
    const c: CustomerRecord | undefined = customers[customerId];
    return c ? customerName(c) : customerId.slice(-6);
  };

  const decisions = worklist.counts.overrides + worklist.counts.confirmations;
  const moneyAndDates =
    worklist.counts['payby-extensions'] +
    worklist.counts['topup-extensions'] +
    worklist.counts['unpaid-artists'] +
    worklist.counts['unclosed-links'];

  return (
    <section className="px-8 py-8" data-testid="ops-worklist">
      <header className="max-w-6xl" data-testid="ops-worklist-header">
        <p className="label-small-caps text-gold-bright">Ops worklist</p>
        <div
          className="flex items-center flex-wrap gap-x-3 gap-y-1 font-inter text-sm mt-1"
          data-testid="ops-header-stats"
        >
          <span data-testid="ops-stat-open" className="text-cream-soft">
            <span className="font-mono font-semibold text-gold-bright">
              {worklist.counts.total}
            </span>{' '}
            open
          </span>
          <span className="text-cream-faint" aria-hidden>
            ·
          </span>
          <span data-testid="ops-stat-decisions" className="text-cream-soft">
            <span className="font-mono font-semibold text-cream">{decisions}</span> decisions
          </span>
          <span className="text-cream-faint" aria-hidden>
            ·
          </span>
          <span data-testid="ops-stat-money" className="text-cream-soft">
            <span className="font-mono font-semibold text-cream">{moneyAndDates}</span> money &amp;
            dates
          </span>
        </div>
      </header>

      {/* Overdue top-ups now lives as a tab in the worklist bar below (tv-6qv.38). */}

      {/* Option B - the section tab-bar runs across the top; the list sits below
          at full width. Each tab is a notification badge with its count. */}
      <nav
        className="mt-6 max-w-6xl flex items-center gap-2 flex-wrap border-b border-gold-dim/25 pb-3"
        data-testid="ops-tab-bar"
        aria-label="Ops worklist sections"
      >
        {TABS.map((meta) => {
          const count =
            meta.key === 'overdue-topups' ? overdueReport.count : worklist.counts[meta.key];
          const isActive = meta.key === active;
          return (
            <button
              key={meta.key}
              type="button"
              onClick={() => setActive(meta.key)}
              data-testid={`ops-tab-${meta.key}`}
              aria-current={isActive || undefined}
              className={worklistTabClass(isActive)}
            >
              <span>{TAB_LABEL[meta.key]}</span>
              <span
                data-testid={`ops-badge-${meta.key}`}
                className={clsx(
                  'min-w-[18px] rounded-full px-1.5 py-0.5 text-center font-mono text-[10px] leading-none',
                  count > 0 ? 'bg-blood text-ink-black' : 'bg-ink-black/40 text-cream-faint',
                )}
              >
                {count}
              </span>
            </button>
          );
        })}
      </nav>

      <div className="mt-5 max-w-6xl" data-testid={`ops-section-${active}`}>
        <div className="flex items-baseline gap-2 mb-1">
          <h2 className="label-small-caps text-gold-bright">{TAB_LABEL[activeMeta.key]}</h2>
          <span className="font-mono text-[11px] text-cream-faint">{activeCount}</span>
          <span className="flex-1 h-px bg-gold-dim/30" />
          <span className="font-inter text-[10px] uppercase tracking-widest text-cream-faint">
            {activeMeta.feeder}
          </span>
        </div>
        <p className="font-serif text-xs text-cream-soft mb-3">{activeMeta.blurb}</p>

        {active === 'overdue-topups' ? (
          overdueReport.count === 0 ? (
            <div className="panel p-8 text-center" data-testid="ops-empty-overdue-topups">
              <p className="font-inter text-sm text-cream-soft">
                No overdue top-ups — nothing at risk right now.
              </p>
            </div>
          ) : (
            <ol className="flex flex-col gap-2" data-testid="ops-list-overdue-topups">
              {overdueReport.rows.map((r) => (
                <li
                  key={r.planId}
                  data-testid={`ops-overdue-row-${r.planId}`}
                  className="panel p-3 flex items-center justify-between gap-3"
                >
                  <span className="font-inter text-sm text-cream">{nameFor(r.customerId)}</span>
                  <span className="font-mono text-xs text-cream-soft">
                    {aud(r.outstandingAud)} · since {simDateShort(r.oldestMissedIso)}
                  </span>
                </li>
              ))}
            </ol>
          )
        ) : activeItems.length === 0 ? (
          <div className="panel p-8 text-center" data-testid={`ops-empty-${active}`}>
            <p className="font-inter text-sm text-cream-soft">Nothing to action here right now.</p>
            <p className="font-inter text-xs text-cream-faint mt-1.5">
              This section is worked to zero.
            </p>
          </div>
        ) : (
          <ol className="flex flex-col gap-2" data-testid={`ops-list-${active}`}>
            {activeItems.map((item) => (
              <OpsRow
                key={item.id}
                item={item}
                nameFor={nameFor}
                artistFor={(id) => artists[id]?.displayName ?? id}
                onAction={setActionItem}
                dmThreads={dmThreads}
                nowIso={clockIso}
                highlighted={item.id === deepItemId}
              />
            ))}
          </ol>
        )}

        {/* Overrides tab folds in the retired /approvals audit trail (tv-i0b):
            the sticky decline-flag banner + the resolved-history disclosure. It sits
            BELOW the items-to-action (tv-6qv.3), the resolved history collapsed by default. */}
        {active === 'overrides' ? (
          <div className="mt-4">
            <OpsOverridesAudit />
          </div>
        ) : null}
      </div>

      {actionItem ? (
        <OpsWorkModal item={actionItem} onClose={() => setActionItem(null)} />
      ) : null}
    </section>
  );
}

// Dense inline tag for worklist rows — the small (text-[9px]) cousin of the
// `.pill-*` component classes, which are sized for headers. `cls` must be
// COLOUR-ONLY utilities (e.g. `bg-blood/15 text-blood`); never pass a `.pill-*`
// class here or its own padding/border/radius double up on this base.
function Pill({ label, cls }: { label: string; cls?: string }) {
  return (
    <span
      className={clsx(
        'inline-flex items-center rounded-full px-1.5 py-0.5 text-[9px] uppercase tracking-widest',
        cls ?? 'bg-cream-soft/15 text-cream-soft',
      )}
    >
      {label}
    </span>
  );
}

function OpsRow({
  item,
  nameFor,
  artistFor,
  onAction,
  dmThreads,
  nowIso,
  highlighted,
}: {
  item: OpsItem;
  nameFor: (id: string) => string;
  artistFor: (id: string) => string;
  onAction: (item: OpsItem) => void;
  dmThreads: Record<string, InternalDmThread>;
  nowIso: string;
  highlighted?: boolean;
}) {
  return (
    <li
      data-testid={`ops-row-${item.id}`}
      className={clsx('panel p-2.5 flex items-center gap-3 flex-wrap', highlighted && 'ring-1 ring-gold-bright')}
    >
      <OpsRowBody item={item} nameFor={nameFor} artistFor={artistFor} dmThreads={dmThreads} nowIso={nowIso} />
      {item.kind === 'unpaid-artist' || item.kind === 'confirmation' ? (
        <OpsThreadPill itemId={item.id} dmThreads={dmThreads} nowIso={nowIso} />
      ) : null}
      {item.kind === 'unclosed-link' && item.readOnly ? (
        <span
          className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] uppercase tracking-widest bg-cream-soft/15 text-cream-soft shrink-0"
          data-testid={`ops-owner-${item.id}`}
        >
          {item.link.owner === 'sweeper' ? 'Sweeper' : 'Agent'} following up
        </span>
      ) : null}
      {opsItemIsActionable(item) ? (
        <button
          type="button"
          onClick={() => onAction(item)}
          className="btn-soft shrink-0"
          data-testid={`ops-action-${item.id}`}
        >
          Action
        </button>
      ) : null}
    </li>
  );
}

function Line({ title, meta }: { title: ReactNode; meta: ReactNode }) {
  return (
    <div className="flex-1 min-w-[200px]">
      <p className="font-inter text-sm text-cream flex items-center gap-2 flex-wrap">{title}</p>
      <p className="font-inter text-[10px] text-cream-soft uppercase tracking-widest mt-1 flex items-center gap-1.5 flex-wrap">
        {meta}
      </p>
    </div>
  );
}

function OpsRowBody({
  item,
  nameFor,
  artistFor,
  dmThreads,
  nowIso,
}: {
  item: OpsItem;
  nameFor: (id: string) => string;
  artistFor: (id: string) => string;
  dmThreads: Record<string, InternalDmThread>;
  nowIso: string;
}) {
  switch (item.kind) {
    case 'override':
      return (
        <>
          <Line
            title={
              <>
                {item.booking ? nameFor(item.booking.customerId) : 'No-deposit request'}
                <Pill label={`Requested by ${actorLabel(item.request.requestedBy)}`} />
              </>
            }
            meta={
              <>
                {item.booking ? <span>{studioDisplay(item.booking.studioId)}</span> : null}
                <span className="normal-case tracking-normal text-cream-soft">
                  &ldquo;{item.request.note}&rdquo;
                </span>
              </>
            }
          />
          <span className="font-mono text-[11px] text-cream-faint">{item.ageLabel}</span>
        </>
      );
    case 'confirmation':
      return (
        <>
          <Line
            title={
              <>
                {nameFor(item.booking.customerId)}
                {item.deadlinePassed ? (
                  <span data-testid={`ops-deadline-passed-${item.id}`}>
                    <Pill label="Deadline passed · cancel" cls="bg-blood text-ink-black font-bold" />
                  </span>
                ) : item.artistAsked ? (
                  <Pill label="Artist asked" cls="bg-azure/15 text-azure" />
                ) : (
                  <Pill label="Escalated" cls="bg-blood/15 text-blood" />
                )}
              </>
            }
            meta={
              <>
                <span>{studioDisplay(item.booking.studioId)}</span>
                <span>{simTimeFull(item.booking.start)}</span>
              </>
            }
          />
        </>
      );
    case 'topup-extension':
      return (
        <>
          <Line
            title={
              <>
                {nameFor(item.plan.customerId)}
                <Pill label="Inside 7 days" cls="bg-blood/15 text-blood" />
              </>
            }
            meta={
              <>
                <span>{studioDisplay(item.plan.studioId)}</span>
                {item.booking ? <span>Appt {simDateShort(item.booking.start)}</span> : null}
                {item.payByIso ? <span>Pay-by {simDateShort(item.payByIso)}</span> : null}
                {item.requestedStartIso ? (
                  <span data-testid={`ops-move-request-${item.id}`} className="text-gold-bright">
                    Client asked to move to {formatStudioDay(item.requestedStartIso, 'EEE d MMM')}
                  </span>
                ) : null}
              </>
            }
          />
        </>
      );
    case 'move-lapsed':
      return (
        <>
          <Line
            title={
              <>
                {nameFor(item.booking.customerId)}
                <Pill label="Date move lapsed" cls="bg-blood/15 text-blood" />
              </>
            }
            meta={
              <>
                <span>{studioDisplay(item.booking.studioId)}</span>
                <span>Old date {simDateShort(item.booking.start)} released</span>
                <span>Money kept on file · Agent calling back</span>
              </>
            }
          />
        </>
      );
    case 'unpaid-artist':
      return (
        <>
          <Line
            title={
              <>
                {item.artistName}
                <Pill label={aud(item.amountAud)} cls="bg-gold/15 text-gold-bright" />
              </>
            }
            meta={
              <>
                <span>{studioDisplay(item.studioId)}</span>
                <span>Owed for {simDateShort(item.dayIso)}</span>
              </>
            }
          />
        </>
      );
    case 'no-rebook':
      return (
        <>
          <Line
            title={<>{nameFor(item.flag.customerId)}</>}
            meta={
              <>
                <span>{studioDisplay(item.flag.studioId)}</span>
                <span className="normal-case tracking-normal text-cream-soft">
                  {item.flag.reason}
                </span>
              </>
            }
          />
        </>
      );
    case 'came-down':
      return (
        <>
          <Line
            title={
              <>
                {nameFor(item.booking.customerId)}
                <Pill label="Not walked down" cls="bg-orange/15 text-orange" />
              </>
            }
            meta={
              <>
                <span>{studioDisplay(item.booking.studioId)}</span>
                <span className="normal-case tracking-normal text-cream-soft">
                  {item.artistName} didn&rsquo;t walk the client down at checkout
                </span>
                {item.booking.checkedOutAt ? <span>{simDateShort(item.booking.checkedOutAt)}</span> : null}
              </>
            }
          />
        </>
      );
    case 'topup':
      return (
        <>
          <Line
            title={
              <>
                {nameFor(item.plan.customerId)}
                <Pill label={DOW[item.dayOfWeek] ?? ''} cls="bg-azure/15 text-azure" />
              </>
            }
            meta={
              <>
                <span>{studioDisplay(item.plan.studioId)}</span>
                <span>Due {simDateShort(item.dueIso)}</span>
                <span>{aud(item.instalment.amountAud)}</span>
              </>
            }
          />
        </>
      );
    case 'unanswered':
      return (
        <>
          <Line
            title={<>{item.customerName}</>}
            meta={
              <>
                <span>{studioDisplay(item.studioId)}</span>
                {item.withAgent && (
                  // Anas 8 Oct: a pushed lead's DM the Agent hasn't answered in 24h.
                  <span data-testid={`ops-unanswered-agent-${item.customerId}`}>
                    {item.agentName ? `Agent hasn’t replied · ${item.agentName}` : 'Agent hasn’t replied'}
                  </span>
                )}
                <span className="normal-case tracking-normal text-cream-soft">
                  {item.lastInbound.body}
                </span>
              </>
            }
          />
          <span className="font-mono text-[11px] text-cream-faint">
            {simTimeFull(item.lastInbound.timestamp)}
          </span>
        </>
      );
    case 'payby-extension':
      return (
        <>
          <Line
            title={
              <>
                {nameFor(item.request.customerId)}
                <Pill label="Deposit link" cls="bg-blood/15 text-blood" />
              </>
            }
            meta={
              <>
                <span>{studioDisplay(item.request.studioId)}</span>
                {item.booking ? <span>Appt {simDateShort(item.booking.start)}</span> : null}
                <span>
                  Pay-by {simDateShort(item.request.currentPayByIso)} → {simDateShort(item.request.requestedPayByIso)}
                </span>
                <span>Requested by {actorLabel(item.request.requestedBy)}</span>
                {item.request.note ? (
                  <span className="normal-case tracking-normal text-cream-soft">&ldquo;{item.request.note}&rdquo;</span>
                ) : null}
              </>
            }
          />
          <span className="font-mono text-[11px] text-cream-faint">{item.ageLabel}</span>
        </>
      );
    case 'unclosed-link':
      return (
        <>
          <Line
            title={
              <>
                {nameFor(item.link.customerId)}
                <Pill label={`${item.link.ageH}h unclosed`} cls="bg-blood/15 text-blood" />
                <Pill
                  label={missedLinkStateLabel(item.link)}
                  cls={item.link.slotState === 'held' ? 'bg-gold/15 text-gold-bright' : item.link.linkState === 'expired' ? 'bg-blood/15 text-blood' : undefined}
                />
              </>
            }
            meta={
              <>
                <span>{studioDisplay(item.link.studioId)}</span>
                {item.link.artistId ? <span>{artistFor(item.link.artistId)}</span> : null}
                {item.link.sessionIso ? <span>{simTimeFull(item.link.sessionIso)}</span> : null}
                <span>{aud(item.link.amountAud)}</span>
                <span>
                  Sent by {linkSenderLabel(item.link.sentBy, item.link.sentByRole)}
                </span>
                {item.readOnly ? null : (
                  <OpsThreadPill itemId={item.id} dmThreads={dmThreads} nowIso={nowIso} showNotMessaged />
                )}
              </>
            }
          />
          <span className="font-mono text-[11px] text-cream-faint">{simTimeFull(item.link.sentAt)}</span>
        </>
      );
    default: {
      // Exhaustiveness guard: a new OpsItem kind must add a branch above.
      void (item satisfies never);
      return null;
    }
  }
}
